import {
  Content,
  ContentNode,
  ContentNodeType,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';

import { getNodeHref } from '../api/ContentUtil';
import { reorderById } from '../common/Arrays';
import { deepCloneContent, deepCloneContentNode } from '../model/CloneContent';
import { findParentPage } from '../components/workflow-viewer/content/FindParentPage';
import { Page, PagePosition } from '../model/Page';

// Maximum depth level for page nesting that the product team allows.
const MAX_PAGE_NESTING_DEPTH = 4;

/** Finds a node by id by walking the page tree (subContent only). Returns undefined if not found. */
export function findNodeInTree(node: ContentNode, id: string): ContentNode | undefined {
  if (node.id === id) return node;
  for (const child of node.subContent) {
    const found = findNodeInTree(child, id);
    if (found) return found;
  }
  return undefined;
}

type PageLocationAttribute = 'href' | 'id';

function nodeTreeContainsLocation(
  node: ContentNode,
  locationId: string,
  attribute: PageLocationAttribute,
): boolean {
  if (node[attribute] === locationId) return true;
  return [...(node.contentSections ?? []), ...(node.subContent ?? [])].some((child) =>
    nodeTreeContainsLocation(child, locationId, attribute),
  );
}

/**
 * Resolves the page rendered for a URL section location.
 * A section location resolves to its containing page.
 */
export function resolvePageForLocation(
  content: Content,
  locationId: string | undefined,
): ContentNode {
  const root = content.contentData;
  if (!locationId) return root;
  const targetLocationId = locationId;

  function findPage(page: ContentNode, attribute: PageLocationAttribute): ContentNode | undefined {
    if (page[attribute] === targetLocationId) return page;
    if (
      (page.contentSections ?? []).some((section) =>
        nodeTreeContainsLocation(section, targetLocationId, attribute),
      )
    ) {
      return page;
    }

    for (const child of page.subContent ?? []) {
      const found = findPage(child, attribute);
      if (found) return found;
    }
    return undefined;
  }

  return findPage(root, 'href') ?? findPage(root, 'id') ?? root;
}

function removeNodeFromTree(parent: ContentNode, id: string): boolean {
  for (let i = 0; i < parent.subContent.length; i++) {
    // @ts-expect-error TS2532 - strict migration
    if (parent.subContent[i].id === id) {
      parent.subContent.splice(i, 1);
      return true;
    }
    // @ts-expect-error TS2345 - strict migration
    if (removeNodeFromTree(parent.subContent[i], id)) return true;
  }
  return false;
}

/** Returns a root Page node wrapping the full page hierarchy of a Guide or Strategy. */
export function listPagesWithRoot(content: Content): Page {
  const pages = listPages(content);
  return { node: content.contentData, depth: 0, children: pages };
}

/** List all pages of the provided document but in sequence, including the root document node. */
export function* enumeratePages(content: Content): Generator<ContentNode> {
  const stack: ContentNode[] = [listPagesWithRoot(content).node];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    yield node;
    for (const child of node.subContent ?? []) {
      stack.push(child);
    }
  }
}

/**
 * Gets a hierarchical list of top-level pages from the provided content.
 * Each page includes its nested children recursively.
 * @throws Error when the content is not a Guide or Strategy.
 */
export function listPages(content: Content): readonly Page[] {
  const type = content.contentMetadata.contentType;
  if (type !== ContentType.Guide && type !== ContentType.Strategy) {
    throw new Error('Only Guide and Strategy content can have pages');
  }

  function buildPages(nodes: readonly ContentNode[], depth: number): Page[] {
    return nodes.map((node) => ({
      node,
      depth,
      children: buildPages(node.subContent, depth + 1),
    }));
  }

  return buildPages(content.contentData.subContent, 0);
}

/**
 * Returns a new Content with a page appended under the given parent (or at root level).
 * Returns undefined if the parentPage is specified but not found in the tree.
 */
export function addPage(
  content: Content,
  title: TiptapOutputType,
  parentPage?: ContentNode,
): Content | undefined {
  const updated: Content = deepCloneContent(content);
  const newPage = ContentNode.create(title, '', ContentNodeType.Page);
  newPage.href = getNodeHref(newPage, updated.contentMetadata) ?? '';

  if (parentPage) {
    const target = findNodeInTree(updated.contentData, parentPage.id);
    if (!target) return undefined;
    target.subContent.push(newPage);
  } else {
    updated.contentData.subContent.push(newPage);
  }
  return updated;
}

/**
 * Returns a new Content with the page removed from the tree.
 * Returns undefined when the pageId is the root document node or is not found.
 */
export function removePage(content: Content, pageId: string): Content | undefined {
  if (content.contentData.id === pageId) return undefined;
  const updated: Content = deepCloneContent(content);
  return removeNodeFromTree(updated.contentData, pageId) ? updated : undefined;
}

/**
 * Returns a new Content with one parent's direct child pages reordered to match `newOrderIds`.
 * Returns undefined when the parent is missing, the ids are not an exact permutation, or the
 * requested order is unchanged.
 */
export function reorderChildPages(
  content: Content,
  parentPageId: string,
  newOrderIds: readonly string[],
): Content | undefined {
  const parent = findNodeInTree(content.contentData, parentPageId);
  if (!parent) return undefined;

  const currentOrderIds = parent.subContent.map((page) => page.id);
  const isExactPermutation =
    newOrderIds.length === currentOrderIds.length &&
    new Set(newOrderIds).size === newOrderIds.length &&
    newOrderIds.every((id) => currentOrderIds.includes(id));
  if (!isExactPermutation) return undefined;
  if (newOrderIds.every((id, index) => currentOrderIds[index] === id)) return undefined;

  const updated = deepCloneContent(content);
  const updatedParent = findNodeInTree(updated.contentData, parentPageId);
  if (!updatedParent) return undefined;
  updatedParent.subContent = reorderById(updatedParent.subContent, newOrderIds);
  return updated;
}

/** Result of a rename operation: the new Content and the renamed page node within it. */
export interface RenameResult {
  readonly content: Content;
  readonly page: ContentNode;
}

/**
 * Returns a new Content with the page's title set and its href regenerated from the new title.
 * Returns undefined when the pageId is the root document node or is not found.
 */
export function renamePage(
  content: Content,
  pageId: string,
  title: TiptapOutputType,
  shortTitle?: TiptapOutputType,
): RenameResult | undefined {
  if (content.contentData.id === pageId) return undefined;
  const updated: Content = deepCloneContent(content);
  const page = findNodeInTree(updated.contentData, pageId);
  if (!page) return undefined;

  page.title = title;
  page.shortTitle = shortTitle;
  // Reset first because getNodeHref short-circuits on a non-empty href.
  page.href = '';
  // @ts-expect-error TS2322 - strict migration
  page.href = getNodeHref(page, updated.contentMetadata);

  return { content: updated, page };
}

/**
 * Finds a section by id within a specific Guide page.
 * Section ids are unique only within a page, so the caller must specify which page to search.
 * Returns undefined when the page is not found or the page has no section with that id.
 */
export function findSectionInPage(
  root: ContentNode,
  pageId: string,
  sectionId: string,
): ContentNode | undefined {
  const page = findNodeInTree(root, pageId);
  if (!page) return undefined;
  return page.contentSections.find((s) => s.id === sectionId);
}

/**
 * Returns a new Content with the Guide page section's title set and its href regenerated.
 * Returns undefined when the page or section is not found.
 */
export function renameSectionInPage(
  content: Content,
  pageId: string,
  sectionId: string,
  title: TiptapOutputType,
  shortTitle?: TiptapOutputType,
): Content | undefined {
  const updated: Content = deepCloneContent(content);
  const section = findSectionInPage(updated.contentData, pageId, sectionId);
  if (!section) return undefined;

  section.title = title;
  section.shortTitle = shortTitle;
  // Reset first because getNodeHref short-circuits on a non-empty href.
  section.href = '';
  // @ts-expect-error TS2322 - strict migration
  section.href = getNodeHref(section, updated.contentMetadata);

  return updated;
}

/**
 * Returns a new Content with the section removed from the Guide page's contentSections.
 * Returns undefined when the page or section is not found.
 */
export function removeSectionInPage(
  content: Content,
  pageId: string,
  sectionId: string,
): Content | undefined {
  const updated: Content = deepCloneContent(content);
  const page = findNodeInTree(updated.contentData, pageId);
  if (!page) return undefined;
  if (!page.contentSections.some((s) => s.id === sectionId)) return undefined;
  page.contentSections = page.contentSections.filter((s) => s.id !== sectionId);
  return updated;
}

/** Result of adding a section: the new Content and the newly created section node. */
export interface AddSectionResult {
  readonly content: Content;
  readonly section: ContentNode;
}

/**
 * Returns a new Content with a new "Untitled" section appended to the Guide page's
 * contentSections, plus the newly created section node so callers can identify it.
 * Returns undefined when the page is not found.
 */
export function addSectionToPage(content: Content, pageId: string): AddSectionResult | undefined {
  const updated: Content = deepCloneContent(content);
  const page = findNodeInTree(updated.contentData, pageId);
  if (!page) return undefined;
  const newSection = ContentNode.create('Untitled', '', ContentNodeType.Section, '');
  page.contentSections.push(newSection);
  return { content: updated, section: newSection };
}

/**
 * Returns the next page in depth-first pre-order traversal starting from the
 * root document, or undefined when the given page is the last page in the tree
 * or is not found.
 */
export function getNextPageInTree(
  content: Content,
  currentPageId: string,
): ContentNode | undefined {
  const flat = flattenPagesDfs(content.contentData);
  const idx = flat.findIndex((n) => n.id === currentPageId);
  if (idx < 0 || idx === flat.length - 1) return undefined;
  return flat[idx + 1];
}

function flattenPagesDfs(node: ContentNode): ContentNode[] {
  const result: ContentNode[] = [node];
  for (const child of node.subContent) {
    result.push(...flattenPagesDfs(child));
  }
  return result;
}

/** Result of a move operation: the modified tree and the page's new position. */
export interface MoveResult {
  readonly root: ContentNode;
  readonly position: PagePosition;
}

/**
 * Returns a new tree with the page swapped with its predecessor sibling, or promoted to the
 * grandparent level when already at index 0. Returns undefined if no-op.
 */
export function movePageUp(root: ContentNode, position: PagePosition): MoveResult | undefined {
  const { parent, index, depth } = position;
  const clonedRoot = deepCloneContentNode(root);

  const clonedParent = findNodeInTree(clonedRoot, parent.id);
  if (!clonedParent) return undefined;

  if (index === 0) {
    const grandparentInfo = findParentPage(clonedRoot, clonedParent);
    if (!grandparentInfo) return undefined;

    const clonedPage = clonedParent.subContent[index];
    clonedParent.subContent.splice(index, 1);
    // @ts-expect-error TS2345 - strict migration
    grandparentInfo.parent.subContent.splice(grandparentInfo.pageIdx, 0, clonedPage);

    return {
      root: clonedRoot,
      position: {
        parent: grandparentInfo.parent,
        index: grandparentInfo.pageIdx,
        depth: depth - 1,
      },
    };
  }

  const clonedPage = clonedParent.subContent[index];
  const newIdx = index - 1;
  const predecessor = clonedParent.subContent[newIdx];
  // @ts-expect-error TS2322 - strict migration
  clonedParent.subContent[newIdx] = clonedPage;
  // @ts-expect-error TS2322 - strict migration
  clonedParent.subContent[index] = predecessor;

  return {
    root: clonedRoot,
    position: { parent: clonedParent, index: newIdx, depth },
  };
}

/**
 * Returns a new tree with the page swapped with its next sibling. Returns undefined if no-op.
 */
export function movePageDown(root: ContentNode, position: PagePosition): MoveResult | undefined {
  const { parent, index, depth } = position;

  if (index >= parent.subContent.length - 1) return undefined;

  const clonedRoot = deepCloneContentNode(root);
  const clonedParent = findNodeInTree(clonedRoot, parent.id);
  if (!clonedParent) return undefined;

  const newIdx = index + 1;
  const page = clonedParent.subContent[index];
  const successor = clonedParent.subContent[newIdx];
  // @ts-expect-error TS2322 - strict migration
  clonedParent.subContent[newIdx] = page;
  // @ts-expect-error TS2322 - strict migration
  clonedParent.subContent[index] = successor;

  return {
    root: clonedRoot,
    position: { parent: clonedParent, index: newIdx, depth },
  };
}

/**
 * Whether a page at the given depth meets the nesting limit so it can be moved deeper.
 * @param depth - 0-indexed depth of the page.
 */
export function isWithinPageDepthLimit(depth: number): boolean {
  return depth < MAX_PAGE_NESTING_DEPTH;
}

/**
 * Returns a new tree with the page nested under the next sibling. Returns undefined if no-op.
 */
export function movePageInto(root: ContentNode, position: PagePosition): MoveResult | undefined {
  const { parent, index, depth } = position;

  if (index >= parent.subContent.length - 1) return undefined;

  const clonedRoot = deepCloneContentNode(root);
  const clonedParent = findNodeInTree(clonedRoot, parent.id);
  if (!clonedParent) return undefined;

  const clonedPage = clonedParent.subContent[index];
  const newParent = clonedParent.subContent[index + 1];
  if (!newParent?.subContent) return undefined;

  // @ts-expect-error TS2345 - strict migration
  newParent.subContent.splice(0, 0, clonedPage);
  clonedParent.subContent.splice(index, 1);

  return {
    root: clonedRoot,
    position: { parent: newParent, index: 0, depth: depth + 1 },
  };
}
